import React from "react";
import { Text } from "rebass/styled-components";
import {
  FaChevronDown,
  FaChevronUp,
  FaUser,
  FaPlus,
  FaCheck,
} from "react-icons/fa";

import { IPlayer, ISuggestedMoment } from "../../types";
import {
  SuggestionsPanel,
  SuggestionsPanelHeader,
  SuggestionsPanelTitle,
  SuggestionsPanelContent,
  SuggestionsGrid,
  SuggestionCard,
  SuggestionAvatar,
  SuggestionInfo,
  SuggestionLabel,
  SuggestionMeta,
  SuggestionAddIcon,
} from "../../styledComponents";

export interface ISuggestedMomentsPanelProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  highlightFocusPlayerId: string | null;
  getPlayerName: (steamId: string) => string;
  suggestedMoments: ISuggestedMoment[];
  addedMomentIds: Set<string>;
  players: IPlayer[];
  addSuggestedMoment: (moment: ISuggestedMoment) => void;
}

export const SuggestedMomentsPanel: React.FC<ISuggestedMomentsPanelProps> = ({
  collapsed,
  onToggleCollapsed,
  highlightFocusPlayerId,
  getPlayerName,
  suggestedMoments,
  addedMomentIds,
  players,
  addSuggestedMoment,
}) => {
  const momentsToShow = highlightFocusPlayerId
    ? suggestedMoments.filter((m) => m.playerId === highlightFocusPlayerId)
    : [];

  return (
    <SuggestionsPanel collapsed={collapsed}>
      <SuggestionsPanelHeader onClick={onToggleCollapsed}>
        <SuggestionsPanelTitle>
          <span>
            {highlightFocusPlayerId
              ? `${getPlayerName(highlightFocusPlayerId)}'s Highlights`
              : "Suggested Highlights"}
          </span>
        </SuggestionsPanelTitle>
        {collapsed ? (
          <FaChevronDown size={12} color="#8fa9af" />
        ) : (
          <FaChevronUp size={12} color="#8fa9af" />
        )}
      </SuggestionsPanelHeader>

      {!collapsed && (
        <SuggestionsPanelContent>
          {!highlightFocusPlayerId ? (
            <Text fontSize="12px" color="#8fa9af" textAlign="center" py={3}>
              Select a player on the left to see their highlights
            </Text>
          ) : momentsToShow.length === 0 ? (
            <Text fontSize="12px" color="#8fa9af" textAlign="center" py={3}>
              No highlights found for this player
            </Text>
          ) : (
            <SuggestionsGrid>
              {momentsToShow.slice(0, 8).map((moment) => {
                const isAdded = addedMomentIds.has(moment.id);
                const player = players.find(
                  (p) => p.steamId === moment.playerId,
                );

                return (
                  <SuggestionCard
                    key={moment.id}
                    $momentType={moment.type}
                    $isAdded={isAdded}
                    onClick={() => !isAdded && addSuggestedMoment(moment)}
                    title={
                      isAdded
                        ? `${moment.playerName} - Already added`
                        : `${moment.playerName} - Click to add ${moment.label}`
                    }
                  >
                    <SuggestionAvatar>
                      {player?.avatar ? (
                        <img src={player.avatar} alt={moment.playerName} />
                      ) : (
                        <FaUser size={12} />
                      )}
                    </SuggestionAvatar>
                    <SuggestionInfo>
                      <SuggestionLabel $momentType={moment.type}>
                        {moment.kills[0]?.killerData?.weapon || ""}{" "}
                        {moment.label}
                      </SuggestionLabel>
                      <SuggestionMeta>
                        <span>Round {moment.round}</span>
                        {moment.headshotCount > 0 && (
                          <>
                            <span className="separator">·</span>
                            <span>{moment.headshotCount} HS</span>
                          </>
                        )}
                      </SuggestionMeta>
                    </SuggestionInfo>
                    <SuggestionAddIcon $isAdded={isAdded}>
                      {isAdded ? <FaCheck size={10} /> : <FaPlus size={10} />}
                    </SuggestionAddIcon>
                  </SuggestionCard>
                );
              })}
            </SuggestionsGrid>
          )}
        </SuggestionsPanelContent>
      )}
    </SuggestionsPanel>
  );
};

export default SuggestedMomentsPanel;
